import {
	isRealPointerMove,
	shouldSendStorefrontHeartbeat,
} from '@supply-mover-2-0/core';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { portalFetch } from '../lib/apiClient';
import { usePortalAuthStore } from '../stores/portalAuthStore';

/** How often the send condition is checked; the helper still sends at most once a minute. */
const CHECK_EVERY_MS = 15 * 1000;

/** Input that shows the shopper is there and reading. */
const INPUT_EVENTS = ['pointerdown', 'wheel', 'touchstart', 'touchmove', 'keydown', 'scroll'] as const;
const MOVE_EVENTS = ['pointermove', 'mousemove'] as const;

/**
 * PD-17191 — the storefront's "still here" heartbeat. Mounted once inside
 * TenantGate. Renders nothing.
 *
 * Every request the storefront makes already counts toward the shopper's
 * visit; this fills in the time spent reading a page without clicking. It
 * sends at most once a minute, and only while a signed-in shopper has the
 * tab visible and has moved the mouse, scrolled, touched or typed in the last
 * 2 minutes, so a tab left open in the background does not keep a visit
 * going or show the shopper as Online now.
 *
 * It watches input itself rather than through the idle sign-out
 * (useIdleTimeout), because that one attaches no listeners on tenants with
 * idle sign-out turned off, and visits must be tracked on every tenant. It
 * uses the same real-pointer-move test, so a page scrolling under a still
 * cursor is not mistaken for the shopper moving it. Unlike the sign-out it
 * counts scrolling, which is what reading a long page looks like.
 *
 * In the storefront mobile app the heartbeat pauses while the app is in the
 * background (owner decision 2026-10-18), from the Capacitor app state as
 * well as page visibility, since a backgrounded web view does not always
 * report itself hidden.
 */
export function StorefrontHeartbeat(): null {
	const isAuthenticated = usePortalAuthStore((s) => s.isAuthenticated);
	const token = usePortalAuthStore((s) => s.token);
	const location = useLocation();

	const lastInputAt = useRef<number | null>(null);
	const lastSentAt = useRef<number | null>(null);
	const lastPointer = useRef<{ x: number; y: number } | null>(null);
	const isBackgrounded = useRef(false);
	const pathRef = useRef(location.pathname);
	const tokenRef = useRef(token);
	pathRef.current = location.pathname;
	tokenRef.current = token;

	useEffect(() => {
		if (!isAuthenticated) {
			return undefined;
		}

		const markInput = (): void => {
			lastInputAt.current = Date.now();
		};
		const onMove = (event: Event): void => {
			const pointer = event as MouseEvent;
			const next = { x: pointer.screenX, y: pointer.screenY };
			if (isRealPointerMove(lastPointer.current, next)) {
				lastPointer.current = next;
				markInput();
			}
		};

		const send = (): void => {
			const now = Date.now();
			if (
				!shouldSendStorefrontHeartbeat({
					now,
					lastInputAt: lastInputAt.current,
					lastSentAt: lastSentAt.current,
					isVisible: document.visibilityState === 'visible',
					isBackgrounded: isBackgrounded.current,
					isAuthenticated: true,
				})
			) {
				return;
			}
			lastSentAt.current = now;
			void portalFetch('/activity/heartbeat', tokenRef.current, {
				method: 'POST',
				body: JSON.stringify({ path: pathRef.current }),
			}).catch(() => {
				// Visit tracking is best effort; a missed heartbeat only shortens
				// the recorded reading time.
			});
		};

		for (const name of INPUT_EVENTS) {
			window.addEventListener(name, markInput, { passive: true, capture: true });
		}
		for (const name of MOVE_EVENTS) {
			window.addEventListener(name, onMove, { passive: true });
		}
		const timer = window.setInterval(send, CHECK_EVERY_MS);

		// The native shell's app state, when there is one. Plain browsers have
		// no Capacitor plugin; the import fails quietly and visibility alone
		// governs.
		let disposed = false;
		let removeAppListener: (() => void) | null = null;
		void (async (): Promise<void> => {
			try {
				const { Capacitor } = await import('@capacitor/core');
				if (!Capacitor.isNativePlatform()) {
					return;
				}
				const { App } = await import('@capacitor/app');
				const listener = await App.addListener('appStateChange', (state) => {
					isBackgrounded.current = !state.isActive;
				});
				if (disposed) {
					void listener.remove();
				} else {
					removeAppListener = (): void => {
						void listener.remove();
					};
				}
			} catch {
				// No native shell.
			}
		})();

		return (): void => {
			disposed = true;
			for (const name of INPUT_EVENTS) {
				window.removeEventListener(name, markInput, { capture: true });
			}
			for (const name of MOVE_EVENTS) {
				window.removeEventListener(name, onMove);
			}
			window.clearInterval(timer);
			removeAppListener?.();
		};
	}, [isAuthenticated]);

	// A route change is input too, and reports the new page promptly
	// ("Viewing: Cart") once the minute since the last heartbeat is up.
	useEffect(() => {
		lastInputAt.current = Date.now();
	}, [location.pathname]);

	return null;
}

